import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Flex,
  Input,
  InputGroup,
  InputRightElement,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Thead,
  Tr,
  HStack,
  Img,
  Image,
  Skeleton,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { formatTableValue } from "./formatTable";
import chevronLeft from "../../assets/images/employee/chevron-left.svg";
import chevronRight from "../../assets/images/employee/chevron-right.svg";
import ReactPaginate from "react-paginate";
import SearchableSelect from "../../components/selectForm";
import { useTransferRequests } from "../../hooks/useTransferRequests";
import useDebounce from "../../hooks/useDebounce";
import { useCategories } from "../../categorys/transferRequests/index";
import toast from "react-hot-toast";
import { ttkqCall } from "../../api/ttkq";
import SearchableSelectMulti from "../../components/selectForm/mutiSelect";
import DatePicker from "react-datepicker";
import calendarIcon from "../../assets/images/onleave/calendar.svg";
import { TypeTransferRequestData } from "../../utils/contants";
import { transferRequestsCall } from "../../api/transferRequests";
import { bookingCall } from "../../api/booking";
import { lockingCall } from "../../api/locking";
import { contractCall } from "../../api/contract";

const TableTransferRequests = ({ isRefetchingWithCreate }) => {
  const statusDatas = [
    {
      key: "PENDING",
      value: "Chưa duyệt",
    },
    {
      key: "REJECTED",
      value: "Không được phê duyệt",
    },
    {
      key: "APPROVED",
      value: "Đã được duyệt",
    },
  ];

  const [search, setSearch] = useState("");
  const [type, setType] = useState(null);
  const [oldEmployeeId, setOldEmployeeId] = useState(null);
  const [newEmployeeId, setNewEmployeeId] = useState(null);
  const [shouldFetch, setShouldFetch] = useState(false);
  const [status, setStatus] = useState(null);
  const [zIndex, setZIndex] = useState(1);
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;

  const debouncedSearchTerm = useDebounce(search, 500);
  const onChangeSearch = useCallback((e) => {
    const { value } = e.target;
    setSearch(value);
  });

  const {
    transferRequestsData,
    refetch,
    isLoading,
    isRefetching,
    totalPages,
    setCurrentPage,
    currentPage,
    entries,
    startEntry,
    endEntry,
  } = useTransferRequests(
    debouncedSearchTerm,
    type,
    oldEmployeeId,
    newEmployeeId,
    status,
    startDate,
    endDate
  );

  // refech create
  function usePrevious(value) {
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    }, [value]);
    return ref.current;
  }
  const prevIsRefetchingWithCreate = usePrevious(isRefetchingWithCreate);
  useEffect(() => {
    if (
      isRefetchingWithCreate &&
      prevIsRefetchingWithCreate !== isRefetchingWithCreate
    ) {
      refetch();
    }
  }, [isRefetchingWithCreate, prevIsRefetchingWithCreate, refetch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShouldFetch(true);
    }, 1200);

    return () => clearTimeout(timer);
  }, []);

  const { constantData } = useCategories(shouldFetch);

  const formatEntries = (entries) =>
    Object.entries(entries || {}).map(([key, value]) => ({ key, value }));

  const employees = useMemo(
    () => formatEntries(constantData?.employee),
    [constantData?.employee]
  );

  const handlePageChange = (selectedPage) => {
    setCurrentPage(selectedPage.selected + 1);
    refetch();
  };

  const tableData = {
    headers: [
      {
        label: "STT",
        key: "",
      },
      {
        label: "Trạng thái",
        key: "status",
      },
      {
        label: "Loại phiếu",
        key: "type",
      },
      {
        label: "Tên nhân tạo phiếu chuyển",
        key: "oldEmployee",
      },
      {
        label: "Tên nhân viện nhận phiếu chuyển",
        key: "newEmployee",
      },
      {
        label: "Dự án",
        key: "project",
      },
      {
        label: "Mã sản phẩm (mã căn)",
        key: "apartment",
      },
      {
        label: "Tên khách hàng",
        key: "customer",
      },
      {
        label: "Ngày chuyển",
        key: "createdAt",
      },
    ],
    data: transferRequestsData,
  };

  const submitStatus = async (status, id) => {
    const toastEdit = toast.loading("Đang cập nhập trạng thái ...");
    const dataFormat = {
      status: status,
    };

    try {
      const data = await transferRequestsCall.updateStatus(id, dataFormat);

      if (data?.type === "BOOKING" && data?.bookingId) {
        const body = {
          bookerId: data?.newEmployeeId,
          currentBooker: data?.oldEmployeeId,
        };
        await bookingCall.transfer(data?.bookingId, body);
      } else if (data?.type === "TTKQ" && data?.ttkqId) {
        const body = {
          creatorId: data?.newEmployeeId,
          currentBooker: data?.oldEmployeeId,
        };
        await ttkqCall.transfer(data?.ttkqId, body);
      } else if (data?.type === "LOCKING" && data?.lockingId) {
        const body = {
          bookerId: data?.newEmployeeId,
          currentBooker: data?.oldEmployeeId,
        };
        await lockingCall.transfer(data?.lockingId, body);
      } else if (data?.type === "SALESCONTRACT" && data?.salesContractId) {
        const body = {
          creatorId: data?.newEmployeeId,
          currentBooker: data?.oldEmployeeId,
        };
        await contractCall.transfer(data?.salesContractId, body);
      }

      await refetch();
      toast.success("Cập nhập trạng thái thành công");
    } catch (error) {
      console.error("Error:", error);
      toast.error("Có lỗi xảy ra khi cập nhập trạng thái");
    }
    toast.dismiss(toastEdit);
  };

  return (
    <>
      <Flex flexDirection={"column"} gap={{ base: "16px" }}>
        <Flex
          backgroundColor="var(--color-main)"
          borderRadius={{ base: "12px" }}
          flexDirection={"column"}
        >
          <Flex
            alignItems={"center"}
            justifyContent={"space-between"}
            gap={{ base: "32px" }}
            p={"16px 16px 12px 16px"}
          >
            <Flex
              alignItems={"flex-start"}
              gap={{ base: "16px 32px" }}
              flex={"1"}
              flexDirection={"column"}
            >
              {/* <InputGroup
                maxW="42vw"
                minW={"42vw"}
                borderRadius={{ base: "8px" }}
                w={{ base: "100%" }}
              >
                <Input
                  placeholder="Nhập mã, tên TTKQ, tên dự án..."
                  type="text"
                  borderRadius={{ base: "8px" }}
                  border={{ base: "1px solid var(--color-boder)" }}
                  backgroundColor={"var(--fill-avatar)"}
                  onChange={onChangeSearch}
                />
                <InputRightElement
                  borderTopRightRadius={"8px"}
                  borderBottomRightRadius={"8px"}
                  pointerEvents="none"
                  cursor={"pointer"}
                  backgroundImage="var(--color-button)"
                >
                  <SearchIcon color="var(--color-main)" />
                </InputRightElement>
              </InputGroup> */}
              <Flex
                alignItems={"center"}
                gap={{ base: "16px 32px" }}
                flex={"1"}
                flexWrap={"wrap"}
                w={"100%"}
              >
                <SearchableSelect
                  isShowSearch={false}
                  options={statusDatas}
                  initialSelectedOption={{
                    key: null,
                    value: "Trạng thái",
                  }}
                  // placeholder={"Trạng thái"}
                  setValue={setStatus}
                />
                <SearchableSelect
                  isShowSearch={true}
                  options={TypeTransferRequestData}
                  initialSelectedOption={{
                    key: 0,
                    value: "Chọn loại",
                  }}
                  setValue={setType}
                />
                <SearchableSelect
                  isShowSearch={true}
                  options={employees}
                  initialSelectedOption={{
                    key: 0,
                    value: "Chọn tên nhân viên tạo phiếu chuyển",
                  }}
                  setValue={setOldEmployeeId}
                  width={"320px"}
                />
                <SearchableSelect
                  isShowSearch={true}
                  options={employees}
                  initialSelectedOption={{
                    key: 0,
                    value: "Chọn tên nhân viên nhận phiếu chuyển",
                  }}
                  setValue={setNewEmployeeId}
                  width={"320px"}
                />
                <InputGroup w={"100%"} maxW={"275px"} zIndex={zIndex}>
                  <DatePicker
                    selectsRange={true}
                    startDate={startDate}
                    endDate={endDate}
                    dateFormat="dd/MM/yyyy"
                    locale="vi"
                    onChange={(update) => {
                      setDateRange(update);
                    }}
                    onFocus={() => {
                      setZIndex(3);
                    }}
                    onBlur={() => {
                      setZIndex(1);
                    }}
                    isClearable={true}
                    placeholderText="Ngày chuyển từ ngày - đến ngày"
                    className="datePickerStyle"
                  />
                  {!startDate && !endDate && (
                    <InputRightElement pointerEvents="none" h={"44px"}>
                      <Image src={calendarIcon} />
                    </InputRightElement>
                  )}
                </InputGroup>
              </Flex>
            </Flex>
          </Flex>
          <Box width={"100%"} height={"100%"}>
            <Skeleton isLoaded={!isLoading && !isRefetching}>
              <TableContainer
                w={"100%"}
                maxH="calc(100vh - 350.5px)"
                overflowY="auto"
              >
                <Table w={"100%"} variant="unstyled">
                  <Thead
                    position="sticky"
                    top="0"
                    zIndex="1"
                    w={"100%"}
                    h={"41px"}
                    color="white"
                    fontWeight="400"
                    backgroundColor="#F9FAFD"
                  >
                    <Tr h={{ base: "41px" }}>
                      {tableData.headers.map((e, index) => {
                        let width = "auto";
                        let textAlign = "auto";
                        if (e.key === "") {
                          width = "2%";
                          textAlign = "center";
                        } else if (e.key === "type") {
                          width = "5%";
                          textAlign = "start";
                        } else if (e.key === "status") {
                          width = "auto";
                          textAlign = "start";
                        } else if (e.key === "apartment") {
                          width = "10%";
                          textAlign = "start";
                        } else if (e.key === "customer") {
                          width = "10%";
                          textAlign = "start";
                        } else if (e.key === "project") {
                          width = "10%";
                          textAlign = "start";
                        } else if (e.key === "oldEmployee") {
                          width = "10%";
                          textAlign = "start";
                        } else if (e.key === "newEmployee") {
                          width = "10%";
                          textAlign = "start";
                        } else {
                          width = "15%";
                          textAlign = "end";
                        }
                        return (
                          <Td
                            p={"16px 16px 8px 16px"}
                            key={index}
                            border={"none"}
                            color={"#51607B"}
                            fontSize={{ base: "14px" }}
                            w={width}
                          >
                            <Box textAlign={"center"} fontWeight={"bold"}>{e.label}</Box>
                          </Td>
                        );
                      })}
                    </Tr>
                  </Thead>
                  <Tbody w={"100%"} h={"100%"}>
                    {tableData.data?.length > 0 ? (
                      tableData.data?.map((e, rowIndex) => {
                        const items = { ...e };
                        delete items.id;
                        const keyValues = Object.keys(items);
                        return (
                          <Tr key={rowIndex} h={"72px"}>
                            <Td
                              p={"16px 0px 8px 16px"}
                              w={"2%"}
                              bg={
                                rowIndex % 2 === 0
                                  ? "var(--color-main)"
                                  : "var(--fill-avatar)"
                              }
                            >
                              {rowIndex + 1}
                            </Td>
                            {keyValues.map((keyvalue, index) => {
                              let width = "auto";
                              let widthText = "100%";
                              let textAlign = "auto";
                              let whiteSpace = "inherit";
                              if (keyvalue === "") {
                                width = "2%";
                                textAlign = "center";
                              } else if (keyvalue === "type") {
                                width = "5%";
                                textAlign = "start";
                              } else if (keyvalue === "status") {
                                width = "auto";
                                textAlign = "start";
                              } else if (keyvalue === "project") {
                                width = "10%";
                                textAlign = "start";
                              } else if (keyvalue === "apartment") {
                                width = "10%";
                                textAlign = "start";
                              } else if (keyvalue === "customer") {
                                width = "10%";
                                textAlign = "start";
                              } else if (keyvalue === "oldEmployee") {
                                width = "10%";
                                textAlign = "start";
                              } else if (keyvalue === "newEmployee") {
                                width = "10%";
                                textAlign = "start";
                              } else {
                                width = "10%";
                                textAlign = "end";
                                whiteSpace = "inherit";
                              }
                              return (
                                <Td
                                  p={"16px 16px 8px 16px"}
                                  w={width}
                                  key={index}
                                  bg={
                                    rowIndex % 2 === 0
                                      ? "var(--color-main)"
                                      : "var(--fill-avatar)"
                                  }
                                >
                                  <Box
                                    fontSize={{ base: "14px" }}
                                    lineHeight={"19.6px"}
                                    textAlign={"start"}
                                    fontWeight={500}
                                    color={"var(--color-info-employee)"}
                                    w={widthText}
                                    whiteSpace={whiteSpace}
                                    overflow="hidden"
                                    textOverflow="ellipsis"
                                    display="-webkit-box"
                                    sx={{
                                      WebkitLineClamp: "2",
                                      WebkitBoxOrient: "vertical",
                                    }}
                                  >
                                    {keyvalue === "status"
                                      ? formatTableValue(
                                          e[keyvalue],
                                          keyvalue,
                                          submitStatus,
                                          e.id
                                        )
                                      : formatTableValue(
                                          e[keyvalue],
                                          keyvalue,
                                          e.code
                                        )}
                                  </Box>
                                </Td>
                              );
                            })}
                          </Tr>
                        );
                      })
                    ) : (
                      <Tr>
                        <Td
                          colSpan={tableData.headers.length}
                          textAlign="center"
                          padding={"40px 0"}
                        >
                          Không có dữ liệu
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </TableContainer>
            </Skeleton>
            <Box mt={10} mr={5} mb={5} ml={5}>
              <Flex alignItems={"center"} justifyContent={"space-between"}>
                <Text>
                  Showing {startEntry} to {endEntry} of {entries} entries
                </Text>
                <HStack spacing={2} justify="flex-end">
                  <ReactPaginate
                    className="pagination-custom"
                    pageCount={totalPages}
                    pageRangeDisplayed={3}
                    marginPagesDisplayed={1}
                    onPageChange={handlePageChange}
                    containerClassName={"pagination"}
                    activeClassName={"active"}
                    breakClassName={"ellipsis"}
                    breakLabel={"..."}
                    previousLabel={
                      <Img
                        w={"16px"}
                        minW={"16px"}
                        h={"16px"}
                        src={chevronLeft}
                      />
                    }
                    nextLabel={
                      <Img
                        w={"16px"}
                        minW={"16px"}
                        h={"16px"}
                        src={chevronRight}
                      />
                    }
                    renderOnZeroPageCount={null}
                    forcePage={currentPage - 1}
                  />
                </HStack>
              </Flex>
            </Box>
          </Box>
        </Flex>
      </Flex>
    </>
  );
};

export default TableTransferRequests;
